/**
 * WALL NICHE (2026-10-07, Matt: "add a recessed niche in a wall for a shower … add a
 * finish surface to each face of it … resized with instance parameters … add shelves
 * to it by instance parameter"). A recess cut into ONE face of its host wall — never
 * through it. Hosted like a window (slides along the wall, set by sill or head height),
 * but it cuts only as deep as the wall's own studs allow (kernel niche.ts#nicheSpec is
 * the one place that rule lives; the geometry and the palette both read it).
 *
 * SIZES ARE CLEAR (finished) SIZES — the inside of the niche after tile. The rough cut
 * adds the wall's own finish build-up on that face (it wraps into the niche on all five
 * sides), and the framing frames that rough opening like a window: studs move to the
 * niche, never the other way round.
 *
 * TYPE = the firm standard (Shower Niche / Wall Niche); every size and dressing is
 * instance, the casework precedent. A Shower Niche type turns on the sloped sill and the
 * tile snap by default.
 */
import type { CategorySchema } from '../../registry'

const FACE_FINISH = (key: string, label: string) =>
  ({ key, label, kind: 'material' as const, default: '' })

export const nicheSchema: CategorySchema = {
  id: 'niche',
  pickRank: 0,
  label: 'Niche',
  hostable: { hostsInto: ['wall'], cutsHost: true },
  typeParams: {
    groups: [
      {
        label: 'Use',
        params: [
          // a shower niche: sloped sill + tile snap default on; a plain wall niche: neither
          { key: 'shower', label: 'Shower Niche', kind: 'bool', default: true },
        ],
      },
    ],
  },
  instanceParams: {
    groups: [
      {
        label: 'Size',
        params: [
          { key: 'widthFt', label: 'Width', kind: 'length', default: 14.5 / 12, min: 4 / 12, max: 8 },
          { key: 'heightFt', label: 'Height', kind: 'length', default: 2, min: 4 / 12, max: 8 },
          // capped at the host wall's stud depth (niche.ts) — never deeper than the wall allows
          { key: 'depthFt', label: 'Depth', kind: 'length', default: 3.5 / 12, min: 1 / 12, max: 2 },
        ],
      },
      {
        label: 'Placement',
        params: [
          { key: 'hostOffsetFt', label: 'Offset Along Wall', kind: 'length', default: 0, min: 0, positional: true },
          // which face of the wall the niche opens to (the side it was placed from)
          {
            key: 'face', label: 'Opens To', kind: 'choice', default: 'interior', positional: true,
            choices: [
              { value: 'interior', label: 'Interior Face' },
              { value: 'exterior', label: 'Exterior Face' },
            ],
          },
          {
            key: 'sillMode', label: 'Set By', kind: 'choice', default: 'sill',
            choices: [
              { value: 'sill', label: 'Sill Height' },
              { value: 'head', label: 'Head Height' },
            ],
          },
          { key: 'sillFt', label: 'Sill Height', kind: 'length', default: 3.5, min: 0, max: 20, conditionalOn: { param: 'sillMode', equals: ['sill'] } },
          { key: 'headHeightFt', label: 'Head Height', kind: 'length', default: 5.5, min: 0, max: 20, conditionalOn: { param: 'sillMode', equals: ['head'] } },
        ],
      },
      {
        label: 'Tile',
        params: [
          // ''/'auto' follows the type (on for a shower niche)
          {
            key: 'tileSnap', label: 'Snap Size to Tile', kind: 'choice', default: '',
            choices: [
              { value: '', label: '(Type Default)' },
              { value: 'on', label: 'On' },
              { value: 'off', label: 'Off' },
            ],
          },
          { key: 'tileWidthFt', label: 'Tile Width', kind: 'length', default: 3 / 12, min: 0.5 / 12, max: 4 },
          { key: 'tileHeightFt', label: 'Tile Height', kind: 'length', default: 6 / 12, min: 0.5 / 12, max: 4 },
          { key: 'groutFt', label: 'Grout Joint', kind: 'length', default: 1 / 16 / 12, min: 0, max: 0.5 / 12 },
        ],
      },
      {
        label: 'Sill',
        params: [
          {
            key: 'sillSlope', label: 'Sloped Sill', kind: 'choice', default: '',
            choices: [
              { value: '', label: '(Type Default)' },
              { value: 'on', label: 'On' },
              { value: 'off', label: 'Off' },
            ],
          },
          // how much lower the sill's front edge sits than its back (drains toward the shower)
          { key: 'sillDropFt', label: 'Sill Drop', kind: 'length', default: 1 / 4 / 12, min: 1 / 32 / 12, max: 1 / 12 },
        ],
      },
      {
        label: 'Edge',
        params: [
          {
            key: 'edge', label: 'Edge Treatment', kind: 'choice', default: 'metal',
            choices: [
              { value: 'metal', label: 'Metal Edge Trim' },
              { value: 'mitered', label: 'Mitered Tile' },
              { value: 'stone', label: 'Stone Sill + Head' },
            ],
          },
          { key: 'stoneProjectFt', label: 'Stone Projection', kind: 'length', default: 0.5 / 12, min: 0, max: 3 / 12, conditionalOn: { param: 'edge', equals: ['stone'] } },
          { key: 'edgeMaterial', label: 'Trim Material', kind: 'material', default: '', conditionalOn: { param: 'edge', equals: ['metal'] } },
        ],
      },
      {
        label: 'Shelves',
        params: [
          { key: 'shelfCount', label: 'Shelves', kind: 'number', default: 0, min: 0, max: 6 },
          {
            key: 'shelfSpacing', label: 'Shelf Spacing', kind: 'choice', default: 'even',
            choices: [
              { value: 'even', label: 'Even' },
              { value: 'custom', label: 'Set Heights' },
            ],
          },
          // feet-inches above the niche sill, comma separated: 10", 1'-8"
          { key: 'shelfHeights', label: 'Shelf Heights', kind: 'text', default: '', conditionalOn: { param: 'shelfSpacing', equals: ['custom'] } },
          { key: 'shelfThicknessFt', label: 'Shelf Thickness', kind: 'length', default: 0.75 / 12, min: 0.25 / 12, max: 2 / 12 },
          { key: 'dividerCount', label: 'Vertical Dividers', kind: 'number', default: 0, min: 0, max: 4 },
          { key: 'dividerThicknessFt', label: 'Divider Thickness', kind: 'length', default: 0.75 / 12, min: 0.25 / 12, max: 2 / 12 },
        ],
      },
      {
        label: 'Lighting',
        params: [
          { key: 'ledStrip', label: 'LED Strip at Head', kind: 'bool', default: false },
        ],
      },
      {
        // '' = the wall's own finish on the face the niche opens to (tile wraps in)
        label: 'Finishes',
        params: [
          FACE_FINISH('finishBack', 'Back'),
          FACE_FINISH('finishLeft', 'Left Side'),
          FACE_FINISH('finishRight', 'Right Side'),
          FACE_FINISH('finishTop', 'Top'),
          FACE_FINISH('finishSill', 'Sill'),
          FACE_FINISH('finishShelf', 'Shelves + Dividers'),
        ],
      },
    ],
  },
}
